import { cn } from "./classNames-DkX30VJU.js";
import { createChangeInputEvent, createPrefixedString } from "./utils-BxEJOnFD.js";
import { mergeRefs } from "./internal/utils/mergeRefs.js";
import { X } from "./icons-DDR9dFSr.js";
import { InputGroup } from "./InputGroup-DnGP9-ZQ.js";
import { useControllableState } from "./useControllableState-BCvUOC6G.js";
import { useRef } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
// src/blocks/TextInput/utils.ts
const genInputTestId = (prefix = "") => {
	const ps = createPrefixedString(prefix);
	return {
		toString: () => prefix,
		inputWrapper: ps`input-wrapper`,
		input: ps`input`,
		clearButton: ps`clear-button`
	};
};
// src/blocks/TextInput/TextInput.tsx
const sanitizeTextInputValue = (value) => {
	if (typeof value === "string" && value.trim() === "") return "";
	return value;
};
/**
* Standard text input with a string-only API, built on `InputGroup`.
*
* **When to use:** plain text / email / password / date fields outside `useAppForm`.
* **When NOT to use:** `FormGroup.TextInput` (labelled group), `field.TextField` (validated
* form), `SearchInput` (search).
*/
const TextInput = ({ ref: forwardedRef, state = "default", className, prefix = null, rightSuffix = null, rightSuffixVariant = "default", leftSuffix = null, allowClear = false, onClear, disabled = false, testId: prefixTestId = "", autoFocus = false, id, name, readOnly, type = "text", focusVariant = "default", onChange, onBlur, onValueChange, onValueBlur, value: valueProp, defaultValue, ...inputProps }) => {
	const inputRef = useRef(null);
	const [value, setValue] = useControllableState({
		prop: valueProp === void 0 ? void 0 : sanitizeTextInputValue(valueProp) ?? "",
		defaultProp: sanitizeTextInputValue(defaultValue) ?? "",
		onChange: onValueChange
	});
	const testId = genInputTestId(prefixTestId);
	const isClearButtonVisible = !!value && !disabled && !readOnly || !!onClear;
	const handleChange = (event) => {
		setValue(event.target.value);
		onChange?.(event);
	};
	const handleBlur = (event) => {
		onValueBlur?.(event.target.value);
		onBlur?.(event);
	};
	const handleClear = () => {
		if (inputRef.current === null) return;
		inputRef.current.value = "";
		inputRef.current.focus();
		onClear?.();
		handleChange(createChangeInputEvent(inputRef.current));
	};
	return /* @__PURE__ */ jsxs(InputGroup, {
		state,
		focusVariant,
		className,
		"data-testid": testId.inputWrapper,
		children: [
			prefix && /* @__PURE__ */ jsx(InputGroup.Addon, {
				align: "inline-start",
				children: prefix
			}),
			/* @__PURE__ */ jsx(InputGroup.Input, {
				...inputProps,
				value,
				autoFocus,
				type,
				onChange: handleChange,
				onBlur: handleBlur,
				ref: mergeRefs(forwardedRef ?? null, inputRef),
				disabled,
				id,
				name,
				readOnly,
				"data-testid": testId.input
			}),
			(leftSuffix || allowClear || rightSuffix) && /* @__PURE__ */ jsxs(InputGroup.Addon, {
				align: "inline-end",
				className: cn({ "tw:min-w-0 tw:shrink": rightSuffixVariant === "addon" }),
				children: [
					leftSuffix,
					allowClear && isClearButtonVisible && /* @__PURE__ */ jsx(InputGroup.Button, {
						"aria-label": "Clear",
						onMouseDown: (e) => e.preventDefault(),
						onClick: handleClear,
						"data-testid": testId.clearButton,
						children: /* @__PURE__ */ jsx(X, { className: "tw:size-4" })
					}),
					rightSuffix && /* @__PURE__ */ jsx("div", {
						className: cn({ "tw:truncate tw:min-w-0 tw:bg-muted tw:px-4 tw:-mr-4 tw:py-2.5": rightSuffixVariant === "addon" }),
						children: rightSuffix
					})
				]
			})
		]
	});
};
export { TextInput };

//# sourceMappingURL=TextInput-BAL-zOAN.js.map